<template>
    <el-table
        style="width: 100%"
        max-height="500"
        :data="receiptLogUnits"
        @row-click="showDetail"
    >
        <el-table-column :show-overflow-tooltip="false" width="60">
            <template slot="header">
                <el-checkbox @change="toggleAllSelection" />
            </template>
            <template slot-scope="scope">
                <el-checkbox
                    v-if="!scope.row.deliveryRequested"
                    :value="isChecked(scope.row)"
                    @change="selectionChange(scope.row, ...arguments)"
                />
                <el-tooltip v-else :open-delay="200" placement="top">
                    <div slot="content">
                        This unit has been requested for delivery.
                    </div>
                    <i class="fa fa-truck" />
                </el-tooltip>
            </template>
        </el-table-column>
        <el-table-column
            v-for="column in tableColumns"
            :key="column.label"
            :width="column.width"
            :min-width="column.minWidth"
            :prop="column.prop"
            :label="column.label"
            :sortable="column.sortable"
            :sort-method="column.sortMethod"
            :formatter="column.formatter"
            :show-overflow-tooltip="true"
        />
    </el-table>
</template>

<script>
import moment from "moment-timezone";
import { Checkbox } from "element-ui";

export default {
    name: "ReceiptLogUnitTable",
    components: {
        [Checkbox.name]: Checkbox,
    },
    props: {
        receiptLog: {
            type: Object,
            default: () => {},
        },
        receiptLogUnits: {
            type: Array,
            required: true,
        },
        clearSelection: {
            type: Boolean,
            required: true,
        },
        receiptLogIdColumn: {
            type: Boolean,
            default: false,
        },
        materialType: {
            type: String,
            default: "",
        },
        storageStart: {
            type: Function,
            default: () => {},
        },
        furnitureFees: {
            type: Function,
            default: () => {},
        },
        formatNumberToCurrency: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            selection: [],
        };
    },
    computed: {
        unRequestedReceiptLogUnits() {
            return this.receiptLogUnits.filter(
                (unit) => !unit.deliveryRequested,
            );
        },
        tableColumns() {
            let columns = [
                {
                    prop: "unitId",
                    label: "Unit Id",
                    sortable: true,
                    width: 100,
                },
                {
                    prop: "description",
                    label: "Description",
                    sortable: true,
                    width: 150,
                },
                {
                    prop: "manufacturer.name",
                    label: "Manufacturer",
                    sortable: true,
                    width: 150,
                },
                {
                    prop: "itemNumber",
                    label: "Item No.",
                    sortable: true,
                    width: 100,
                },
                {
                    prop: "quantity",
                    label: "Qty",
                    sortable: true,
                    width: 100,
                },
                {
                    prop: "date",
                    label: "Received",
                    formatter: (_row, _column, value) => this.formatDate(value),
                    sortable: true,
                    sortMethod: (a, b) => new Date(b.date) - new Date(a.date),
                    width: 150,
                },
                {
                    prop: "relatedNumber",
                    label: "S/N",
                    sortable: true,
                    width: 100,
                },
                {
                    prop: "storageStart",
                    label: "Storage Start",
                    formatter: (row) =>
                        this.formatDate(
                            this.storageStart(this.receiptLog, row),
                        ),
                    sortable: true,
                    sortMethod: (a, b) => this.sortStargeStart(a, b),
                    width: 150,
                },
                {
                    prop: "storageCost",
                    label: "Storage Cost",
                    formatter: (row) =>
                        this.formatNumberToCurrency(
                            this.furnitureFees(this.receiptLog, row),
                        ),
                    sortable: true,
                    sortMethod: (a, b) => this.sortStorageCost(a, b),
                    width: 150,
                },
                {
                    prop: "lotNumber",
                    label: "Lot/Sticker No.",
                    sortable: true,
                    formatter: (row, _column, value) => {
                        if (value && row.stickerNumber) {
                            return `${value} - ${row.stickerNumber} `;
                        }
                        if (value) return value;
                        if (row.stickerNumber) return row.stickerNumber;
                        return "--";
                    },
                    width: 150,
                },
                {
                    prop: "deliveryLogId",
                    label: "Delivery Log",
                    sortable: true,
                    width: 150,
                },
                {
                    prop: "damaged",
                    label: "Damaged",
                    formatter: (_row, _column, value) =>
                        this.formatDamaged(value),
                    sortable: true,
                    width: 150,
                },
            ];
            if (this.materialType !== "Furniture") {
                columns = columns.filter(
                    (column) =>
                        column.prop !== "storageStart" &&
                        column.prop !== "storageCost",
                );
            }
            if (this.receiptLogIdColumn) {
                columns.unshift({
                    prop: "receiptLogId",
                    label: "Receipt Log Id",
                    sortable: true,
                });
            }
            return columns;
        },
    },
    watch: {
        clearSelection(clear) {
            if (clear) {
                this.selection = [];
            }
        },
        selection(items) {
            this.$emit("selectionChange", items);
        },
    },
    methods: {
        formatDate(date) {
            if (date) {
                return moment(date).format("MM/DD/YYYY");
            }
            return "--";
        },
        formatDamaged(damaged) {
            if (damaged) {
                return "yes";
            }
            return "no";
        },
        sortStargeStart(a, b) {
            const date2 = new Date(this.storageStart(this.receiptLog, b));
            const date1 = new Date(this.storageStart(this.receiptLog, a));
            return date2 - date1;
        },
        sortStorageCost(a, b) {
            const cost2 = Number(this.furnitureFees(this.receiptLog, b));
            const cost1 = Number(this.furnitureFees(this.receiptLog, a));
            return cost2 - cost1;
        },
        showDetail(row, column, $event) {
            if ($event.target.className.includes("el-checkbox")) return;
            this.$router.push(`/receipt/${row.receiptLogId}/${row.unitId}`);
        },
        isChecked(item) {
            // eslint-disable-next-line max-len
            return (
                this.selection.findIndex(
                    (unit) =>
                        item.unitId === unit.unitId &&
                        item.receiptLogId === unit.receiptLogId,
                ) !== -1
            );
        },
        selectionChange(item, checked) {
            if (checked) {
                this.selection.push(item);
            } else {
                // eslint-disable-next-line max-len
                const index = this.selection.findIndex(
                    (i) =>
                        i.unitId === item.unitId &&
                        i.receiptLogId === item.receiptLogId,
                );
                if (index !== -1) {
                    this.selection.splice(index, 1);
                }
            }
        },
        toggleAllSelection(checked) {
            if (checked) {
                this.selection = [...this.unRequestedReceiptLogUnits];
            } else {
                this.selection = [];
            }
        },
    },
};
</script>
